import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button, Input, Card } from '../components/ui';
import { Mail, Lock, User, Eye, EyeOff, Loader2, AlertTriangle } from 'lucide-react';

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, register, loginWithGoogle } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!isLogin && !formData.name.trim()) {
      setError(t('auth.nameRequired', 'Name is required'));
      return;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      setError(t('auth.invalidEmail', 'Please enter a valid email'));
      return;
    }
    if (formData.password.length < 6) {
      setError(t('auth.passwordMin', 'Password must be at least 6 characters'));
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
      } else {
        await register(formData.name, formData.email, formData.password);
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || t('auth.loginFailed', 'Login failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    loginWithGoogle();
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 hero-glow opacity-30" />
      <div 
        className="absolute inset-0 opacity-10 bg-cover bg-center"
        style={{ 
          backgroundImage: `url('https://images.pexels.com/photos/3327157/pexels-photo-3327157.jpeg?auto=compress&w=1920')` 
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <Link to="/" className="block text-center mb-8">
          <span className="font-heading text-3xl">
            INK<span className="text-primary">TRACK</span>
          </span>
        </Link>

        <Card className="p-8">
          {/* Tabs */}
          <div className="flex mb-8 border-b border-border">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 pb-4 text-sm font-heading tracking-wider border-b-2 transition-colors ${
                isLogin ? 'text-primary border-primary' : 'text-muted-foreground border-transparent'
              }`}
              data-testid="login-tab"
            >
              {t('auth.login', 'Sign In')}
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 pb-4 text-sm font-heading tracking-wider border-b-2 transition-colors ${
                !isLogin ? 'text-primary border-primary' : 'text-muted-foreground border-transparent'
              }`}
              data-testid="register-tab"
            >
              {t('auth.register', 'Sign Up')}
            </button>
          </div>

          {/* Google Login */}
          <Button
            variant="outline"
            className="w-full mb-6"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {t('auth.google', 'Sign in with Google')}
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-background-secondary px-2 text-muted-foreground">
                {t('auth.orContinueWith', 'or')}
              </span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  name="name"
                  placeholder={t('auth.name', 'Full Name')}
                  value={formData.name}
                  onChange={handleChange}
                  className="pl-12"
                  required={!isLogin}
                  data-testid="name-input"
                />
              </div>
            )}
            
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="email"
                name="email"
                placeholder={t('auth.email', 'Email')}
                value={formData.email}
                onChange={handleChange}
                className="pl-12"
                required
                data-testid="email-input"
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder={t('auth.password', 'Password')}
                value={formData.password}
                onChange={handleChange}
                className="pl-12 pr-12"
                required
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {error && (
              <p className="text-destructive text-sm" data-testid="auth-error">
                {error}
              </p>
            )}

            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                isLogin ? t('auth.loginBtn', 'Sign In') : t('auth.registerBtn', 'Sign Up')
              )}
            </Button>
          </form>

          {isLogin && (
            <p className="text-center text-sm text-muted-foreground mt-6">
              <span className="text-muted-foreground/50 cursor-not-allowed">
                {t('auth.forgotPassword', 'Forgot Password (Coming Soon)')}
              </span>
            </p>
          )}
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6">
          {isLogin ? t('auth.noAccount', "Don't have an account? ") : t('auth.hasAccount', 'Already have an account? ')}
          <button
            onClick={() => setIsLogin(!isLogin)}
            className="text-primary hover:underline"
          >
            {isLogin ? t('auth.register', 'Sign Up') : t('auth.login', 'Sign In')}
          </button>
        </p>

        <p className="text-center text-sm text-muted-foreground mt-3">
          <Link to="/artist/login" className="text-primary/70 hover:text-primary hover:underline">
            {t('auth.artistPortal', 'Are you a tattoo artist? Sign in here')}
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

export default Login;
